import React from 'react';
import { AlertTriangle, ArrowLeft, Home, Lock, Search } from 'lucide-react';
import Logo from '../assets/favicon.jpeg';

type ErrorKind = '403' | '404';

interface ErrorPageProps {
  kind: ErrorKind;
  inDashboard?: boolean;
  onBack?: () => void;
  onHome?: () => void;
}

const COPY: Record<ErrorKind, {
  eyebrow: string;
  title: string;
  message: string;
  icon: React.ReactNode;
  accent: string;
  soft: string;
}> = {
  '403': {
    eyebrow: 'Permission blocked',
    title: '403 Forbidden',
    message: 'Your account does not have permission to view this dashboard area. Use an account with the right role or return to an allowed page.',
    icon: <Lock size={34} />,
    accent: '#dc2626',
    soft: '#fef2f2',
  },
  '404': {
    eyebrow: 'Page not found',
    title: '404 Not Found',
    message: 'The page you are looking for does not exist, moved, or is not available from this dashboard.',
    icon: <Search size={34} />,
    accent: '#F1561D',
    soft: '#fff5f2',
  },
};

const ErrorPage: React.FC<ErrorPageProps> = ({ kind, inDashboard = false, onBack, onHome }) => {
  const copy = COPY[kind];

  return (
    <section
      style={{
        minHeight: inDashboard ? 'calc(100vh - 104px)' : '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: inDashboard ? '32px' : '24px',
        background: inDashboard ? '#f9fafb' : '#ffffff',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '760px',
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 18px 45px rgba(17, 24, 39, 0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            minHeight: '8px',
            background: copy.accent,
          }}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: '28px',
            padding: '36px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src={Logo}
              alt="ScanPayGo"
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '8px',
                objectFit: 'cover',
              }}
            />
            <div>
              <p style={{ margin: 0, color: '#111827', fontWeight: 700, fontSize: '15px' }}>
                Scan Pay Go
              </p>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '12px' }}>
                Admin Dashboard
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
            <div
              aria-hidden="true"
              style={{
                width: '68px',
                height: '68px',
                borderRadius: '8px',
                background: copy.soft,
                color: copy.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {copy.icon}
            </div>

            <div style={{ minWidth: 0 }}>
              <p
                style={{
                  margin: '0 0 8px',
                  color: copy.accent,
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {copy.eyebrow}
              </p>
              <h1
                style={{
                  margin: '0 0 12px',
                  color: '#111827',
                  fontSize: 'clamp(34px, 5vw, 56px)',
                  lineHeight: 1,
                  letterSpacing: 0,
                }}
              >
                {copy.title}
              </h1>
              <p
                style={{
                  margin: 0,
                  color: '#4b5563',
                  fontSize: '16px',
                  lineHeight: 1.65,
                  maxWidth: '540px',
                }}
              >
                {copy.message}
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              paddingTop: '4px',
            }}
          >
            {onHome && (
              <button
                type="button"
                onClick={onHome}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  minHeight: '42px',
                  padding: '0 16px',
                  borderRadius: '8px',
                  border: '1px solid transparent',
                  background: copy.accent,
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '14px',
                }}
              >
                <Home size={16} />
                Dashboard Home
              </button>
            )}

            {onBack && (
              <button
                type="button"
                onClick={onBack}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  minHeight: '42px',
                  padding: '0 16px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#ffffff',
                  color: '#374151',
                  fontWeight: 700,
                  fontSize: '14px',
                }}
              >
                <ArrowLeft size={16} />
                Go Back
              </button>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '14px 16px',
              borderRadius: '8px',
              background: '#f9fafb',
              border: '1px solid #f3f4f6',
              color: '#6b7280',
              fontSize: '13px',
              lineHeight: 1.5,
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0, color: copy.accent }} />
            <span>
              If this blocks normal work, confirm the user role and assigned store in User Management.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ErrorPage;
